const enUS = {
  'meta.title': 'Image to Bead Pattern Generator | Local-first Web App',
  'meta.description': 'Turn images into editable, printable fuse-bead patterns with per-cell color codes, board guides, and material counts. Images stay in your browser.',
  'meta.ogTitle': 'Bead Grid Studio · Image to Fuse Bead Pattern',
  'meta.ogDescription': 'Match colors, count beads, edit the grid, and export a printable pattern. Everything is processed locally.',
  'meta.ogLocale': 'en_US',
  'meta.schemaName': 'Bead Grid Studio',
  'meta.schemaDescription': 'A local-first fuse bead pattern generator that turns images into editable, printable bead patterns.',

  'app.heading': 'Image to Bead Pattern Generator',
  'app.skipCanvas': 'Skip to pattern canvas',
  'brand.aria': 'Bead Pattern Generator',
  'brand.name': 'Bead Pattern Generator',
  'brand.tagline': 'Image to bead pattern',
  'home.kicker': 'Local processing · No account required',
  'home.title': 'Image to Bead Pattern',
  'home.subtitle': 'Upload an image to generate a bead pattern, review colors, codes, and bead counts.',
  'home.privacy': 'Images are processed locally in your browser and are not automatically uploaded.',
  'home.entries': 'Conversion entries',
  'home.back': 'Home',
  'home.entryImage': 'Image to Beads',
  'home.entryImageText': 'Turn an ordinary image into a bead pattern.',
  'home.entryPortrait': 'Portrait to Beads',
  'home.entryPortraitText': 'Use photo-friendly defaults for portrait images.',
  'home.entryPixelArt': 'Image to Pixel Art',
  'home.entryPixelArtText': 'Convert an ordinary image into pixel art.',
  'home.entryPixelBead': 'Pixel Art to Beads',
  'home.entryPixelBeadText': 'Convert pixel art into a bead pattern.',
  'language.label': 'Interface language',
  'language.chinese': '中文',
  'language.english': 'EN',
  'language.japanese': 'JA',
  'language.korean': 'KO',
  'language.french': 'FR',
  'project.untitled': 'Untitled pattern',
  'project.localOnly': 'Images are processed only in this browser',
  'project.imageLocal': '{width} × {height} · processed locally',

  'action.chooseImage': 'Choose Image',
  'action.trySample': 'Try a Sample',
  'action.downloadOffline': 'Download Offline',
  'action.exportPattern': 'Export Pattern',
  'action.exportBuildSheet': 'Export Build Sheet',
  'action.adjust': 'Adjust Settings',
  'action.restoreFullImage': 'Restore Full Image',
  'action.applyRecommended': 'Use Recommended Size',
  'action.cropRegion': 'Crop One Region',
  'action.regenerate': 'Regenerate with These Settings',
  'action.applySize': 'Apply Size',
  'action.saveProject': 'Save Editable Project (No Source Image)',
  'action.loadProject': 'Open Project',
  'action.exportPng': 'Export Coded Pattern PNG',
  'action.print': 'Print / Paginate',
  'action.restoreDraft': 'Restore Unfinished Pattern',
  'action.clearDraft': 'Clear Recovery Data',
  'action.cancel': 'Cancel',
  'action.close': 'Close',
  'action.ok': 'Got It',
  'action.resetCrop': 'Reset to Full Image',
  'action.applyCrop': 'Apply and Regenerate',
  'action.cancelConversion': 'Cancel Conversion',
  'action.share': 'Share',
  'action.copyLink': 'Copy Link',
  'action.exportShareCard': 'Export Share Card',
  'action.startMaking': 'Start Making',
  'action.exportCsv': 'Export CSV',
  'action.markColorDone': 'Mark Color Done',
  'action.markColorUndone': 'Mark as Not Done',
  'action.showAllColors': 'Show All',
  'action.resetMaking': 'Reset Progress',
  'action.star': 'Star on GitHub',
  'action.reportIssue': 'Report an Issue',
  'action.contribute': 'Contribute',

  'aria.help': 'Help, privacy, and version',
  'aria.undo': 'Undo',
  'aria.redo': 'Redo',
  'aria.zoom': 'Canvas zoom',
  'aria.zoomOut': 'Zoom out',
  'aria.zoomIn': 'Zoom in',
  'aria.controls': 'Pattern settings',
  'aria.closeControls': 'Close settings',
  'aria.steps': 'How it works',
  'aria.sizeMode': 'Sizing method',
  'aria.presets': 'Pattern long-side presets',
  'aria.boardLayout': 'Pegboard layout',
  'aria.tools': 'Drawing tools',
  'aria.stage': 'Pattern preview and editor',
  'aria.preview': 'Preview mode',
  'aria.viewOptions': 'View options',
  'aria.paletteStats': 'Palette and material counts',
  'aria.closePalette': 'Close palette',
  'aria.paletteRange': 'Palette provider',
  'aria.paletteSeries': 'Filter by color family',
  'aria.searchPalette': 'Search palette',
  'aria.batchReplace': 'Batch replace colors',
  'aria.projectLicense': 'Project and licenses',
  'aria.mobilePanels': 'Mobile panels',
  'aria.closeCrop': 'Close crop dialog',
  'aria.cropPreview': 'Image crop preview',
  'aria.closeProduct': 'Close product information',
  'aria.printSheet': 'Printable pattern',
  'aria.printImage': 'Fuse bead pattern prepared for printing',
  'aria.codesOff': 'Hide per-cell color codes',
  'aria.codesOn': 'Show per-cell color codes',
  'aria.codesZoom': 'Color codes are on; zoom in to view them',
  'aria.makingProgress': 'Fuse-bead making progress',
  'aria.makingActions': 'Making assistant controls',
  'aria.previousColor': 'Previous color',
  'aria.nextColor': 'Next color',
  'aria.closeMaking': 'Close making assistant',
  'aria.focusColor': 'Focus {code}, {count} beads',
  'aria.markColorDone': 'Mark {code} as completed',
  'aria.markColorUndone': 'Mark {code} as not completed',
  'title.undo': 'Undo Ctrl+Z',
  'title.redo': 'Redo Ctrl+Y',
  'title.toggleGrid': 'Show or hide grid',
  'title.toggleRuler': 'Show or hide coordinate rulers',
  'title.toggleCodes': 'Show or hide per-cell color codes',
  'title.codesToggle': 'Show or hide per-cell color codes',
  'title.codesZoom': 'Color codes are on; zoom until each cell is at least 14 pixels',
  'title.fitCanvas': 'Fit the whole pattern in the current window',

  'panel.tools': 'Tools and Settings',
  'panel.image': 'Image and Conversion',
  'panel.localProcessing': 'Local processing',
  'dropzone.title': 'Choose an image · Generate a bead pattern',
  'dropzone.note': 'Detects the image type, calibrates black and white, and preserves aspect ratio. Verify your real pegboard and physical color chart before making.',
  'smart.kicker': 'One-click mode',
  'smart.calculating': 'Calculating a recommended setup',
  'smart.defaultSummary': 'Automatically chooses the size, conversion mode, palette, and preview.',
  'smart.step1': '1 Add image',
  'smart.step2': '2 Review pattern',
  'smart.step3': '3 Export',
  'smart.generate': 'Generate Automatically',
  'advice.kicker': 'Capacity Check',
  'advice.title': 'Crop this image to a smaller subject',
  'advice.text': 'The current grid cannot preserve every detail in this complex image.',
  'advice.perBead': 'source pixels per bead',
  'advice.expressiveness': 'current capacity',
  'advanced.title': 'Advanced Settings',
  'advanced.autoConfigured': 'Configured by one-click mode',
  'field.processMode': 'Conversion Mode',
  'mode.cartoon': 'Icons and Line Art · Preserve Outlines',
  'mode.detail': 'Detailed Photo · Preserve Shadows',
  'mode.document': 'Document Structure · Preserve Layout',
  'mode.photo': 'Natural Photo · Area Average',
  'mode.pixel': 'Pixel Sampling · No Smoothing',
  'modeHint.cartoon': 'For cartoons, line art, and logos. Preserves black outlines and small saturated details.',
  'modeHint.detail': 'For portraits, pets, and complex photos. Boosts local contrast while protecting dark structure.',
  'modeHint.document': 'For papers, screenshots, and diagrams. Preserves long rules and large fills while suppressing tiny text; body text will not stay readable.',
  'modeHint.photo': 'For photos, gradients, and soft lighting. Averages each cell in linear-light space.',
  'modeHint.pixel': 'For images that are already pixel art. Reads the center pixel of each cell without smoothing.',
  'field.fitMode': 'Image Fit',
  'fit.cover': 'Fill and Crop',
  'fit.contain': 'Show Entire Image',
  'field.lightBackground': 'Light Background',
  'white.auto': 'Remove Automatically',
  'white.keep': 'Keep White Beads',
  'field.referenceOpacity': 'Reference Image Overlay',
  'field.maxColors': 'Maximum Colors in This Pattern',
  'field.maxColorsNote': 'Automatic matching chooses the closest colors from the active palette. This limits colors used by this pattern, not the palette size.',
  'field.mergeStrength': 'Merge Similar Colors',
  'field.mergeNote': 'Higher values use fewer near-duplicate colors while preserving dark outlines and saturated accents.',
  'field.protectDark': 'Neutral Black Protection',
  'field.protectDarkNote': 'Prevents compressed or reflective black pixels from being mistaken for deep blue, brown, or purple.',

  'size.title': 'Pattern Size',
  'size.subtitle': 'Pattern and pegboard are separate',
  'size.patternMode': 'By Pattern Detail',
  'size.boardMode': 'By Physical Pegboard',
  'size.patternWidth': 'Pattern Width',
  'size.patternHeight': 'Pattern Height',
  'size.lockAspect': 'Lock Image Aspect Ratio (Recommended)',
  'size.aspectInitial': 'After choosing an image, presets set the pattern’s longest side instead of forcing it into a square.',
  'size.longSide': ({ size }) => `Long Side ${size}`,
  'board.profile': 'Physical Pegboard',
  'board.mini52': 'Common Mini 2.6 mm · 52×52 cells / board',
  'board.mini26': 'Small Mini 2.6 mm · 26×26 cells / board',
  'board.artkal50': 'Artkal Mini 2.6 mm · 50×50 cells / board',
  'board.artkal78': 'Artkal Mini 2.6 mm · 78×78 cells / board',
  'board.midi29': 'Standard 5 mm · 29×29 cells / board',
  'board.midi14': 'Small 5 mm · 14×14 cells / board',
  'board.layout.1x1': '1×1 board',
  'board.layout.2x1': '2 boards wide',
  'board.layout.1x2': '2 boards tall',
  'board.layout.2x2': '2×2 boards',
  'board.layout.3x2': '3×2 boards',
  'board.layout.2x3': '2×3 boards',
  'board.initialTitle': '52×52 board · Pattern keeps its aspect ratio',
  'board.initialText': 'After choosing an image, this shows the occupied pattern size, margins, board count, and finished dimensions.',
  'board.compatibilityNote': '50-cell and 52-cell boards may use incompatible pin spacing. Select the exact physical board you own; this tool never treats different formats as interchangeable.',

  'tools.title': 'Draw and Transform',
  'tools.subtitle': 'Drag to draw continuously',
  'tool.brush': 'Brush B',
  'tool.eraser': 'Eraser E',
  'tool.picker': 'Eyedropper I',
  'tool.mirrorH': 'Flip Horizontally',
  'tool.mirrorV': 'Flip Vertically',
  'tool.rotate': 'Rotate 90° Clockwise',
  'tool.clear': 'Clear Pattern',
  'output.title': 'Project and Output',
  'output.subtitle': 'PNG / JSON / Print',
  'output.majorGrid': 'Bold Guide Interval',
  'output.major5': 'Every 5 cells',
  'output.major10': 'Every 10 cells (Recommended)',
  'output.major29': 'Every 29 cells (Common 5 mm board)',
  'output.majorNote': 'The screen preview and construction sheet use the same bold guides for locating cells and joining boards.',

  'preview.square': 'Color Blocks',
  'preview.bead': 'Bead Preview',
  'view.grid': 'Grid',
  'view.ruler': 'Rulers',
  'view.codes': 'Codes',
  'view.fit': 'Fit',
  'quality.title': 'This image exceeds the grid capacity',
  'quality.text': 'Body text and thin lines will not remain clear',
  'quality.crop': 'Crop One Subject',

  'hero.title': 'Image → Fuse Bead Pattern',
  'hero.text': 'Drop in an image, match bead colors, count materials, and export a per-cell printable pattern.',
  'hero.flow1': 'Drop in an image',
  'hero.flow2': 'Generate bead pattern',
  'hero.flow3': 'Match colors',
  'hero.flow4': 'Count beads',
  'hero.flow5': 'Export pattern',
  'hero.noInstall': 'No installation',
  'hero.noAccount': 'No account',
  'hero.noUpload': 'No image upload',
  'hero.offline': 'Works offline',
  'hero.local': '100% processed locally in your browser',
  'draft.restore': 'Restore Unfinished Pattern',
  'draft.clear': 'Clear Recovery Data',

  'legend.title': 'Pattern Legend',
  'legend.empty': 'Color codes and counts appear after conversion',
  'legend.codesZoom': 'Zoom in to inspect color codes',
  'canvas.help': 'Use arrow keys to move the keyboard cursor, Space to draw with the current tool, and Delete to erase the current cell.',
  'palette.panel': 'Palette and Materials',
  'palette.title': 'Palette',
  'palette.provider': 'Standard 221',
  'palette.providerMard': 'MARD-compatible Base 221',
  'palette.countAll': '221 colors · 9 families',
  'palette.countSeries': '{series} family · {count} colors',
  'palette.all': 'All',
  'palette.search': 'Search code or color family',
  'palette.replaceSource': 'Source color',
  'palette.replaceTarget': 'Replace with',
  'palette.replaceAll': 'Replace all',
  'palette.note': 'The default palette uses 221 standard codes (A/B/C/D/E/F/G/H/M). H1 is transparent and reserved for manual drawing. Automatic conversion uses the remaining solid colors. Screen HEX values are references only; verify physical beads with the batch you buy.',
  'palette.transparent': 'Transparent',
  'palette.white': 'White',
  'palette.black': 'Black',
  'palette.series.A': 'Yellow and Orange',
  'palette.series.B': 'Green',
  'palette.series.C': 'Blue and Cyan',
  'palette.series.D': 'Blue and Purple',
  'palette.series.E': 'Pink and Rose',
  'palette.series.F': 'Red',
  'palette.series.G': 'Brown and Skin Tone',
  'palette.series.H': 'Neutral',
  'palette.series.M': 'Earth Tone',
  'palette.series.N': 'Neutral',
  'palette.series.R': 'Red',
  'palette.series.O': 'Orange',
  'palette.series.Y': 'Yellow',
  'palette.series.T': 'Teal',
  'palette.series.P': 'Purple',
  'palette.series.X': 'Transparent',
  'stats.title': 'Material Counts',
  'stats.sorted': 'Sorted by quantity',
  'stats.total': 'Total Beads',
  'stats.colors': 'Colors Used',
  'stats.empty': 'Empty Cells',
  'stats.emptyList': 'After you add an image or draw on the canvas, color quantities appear here.',
  'stats.copyList': 'Copy bead list',
  'stats.listTitle': 'Fuse bead shopping list (Bead Grid Studio)',
  'stats.listMeta': 'Pattern: {cols} × {rows} cells · {total} beads total · {colors} colors',
  'stats.listLine': '{code} {name} · {count} beads · {percent}',
  'stats.listTip': 'Colors can vary between brands and batches; verify against a physical color card before ordering and keep about 10% spare per color.',
  'making.kicker': 'Making Assistant',
  'making.title': 'Place beads one color at a time',
  'making.chooseColor': 'Choose a color from the material list',
  'making.progress': '{done}/{total} beads · {colorsDone}/{totalColors} colors · {percent}%',
  'making.progressAria': '{percent}% complete, {colorsDone} of {totalColors} colors',
  'making.focusedCount': 'Verify {count} beads in this color',
  'making.allColors': 'Showing all colors',
  'making.allColorsDetail': 'Select a material row to focus a color again',
  'making.allDone': 'Every color is complete',
  'making.allDoneDetail': 'Run one final check against the build sheet',
  'csv.code': 'Code',
  'csv.name': 'Color name',
  'csv.hex': 'Screen-reference HEX',
  'csv.count': 'Count',
  'csv.completed': 'Completed',
  'csv.yes': 'Yes',
  'csv.no': 'No',

  'status.ready': 'Ready · All processing happens on this device',
  'footer.privacy': 'Privacy',
  'footer.terms': 'Terms',
  'footer.license': 'License',
  'footer.notices': 'Third-party Notices',
  'mobile.tools': 'Tools',
  'mobile.pattern': 'Pattern',
  'mobile.palette': 'Palette',
  'mobile.stats': 'Counts',

  'crop.title': 'Crop the Area You Want to Make',
  'crop.help': 'Drag a rectangle over the image or enter exact percentages below. For papers, screenshots, or multi-panel images, choose one subject at a time. A tighter crop gives every bead more useful structure.',
  'crop.ratioLabel': 'Crop ratio',
  'crop.original': 'Original ratio',
  'crop.precise': 'Exact crop (percent of source image)',
  'crop.left': 'Left %',
  'crop.top': 'Top %',
  'crop.width': 'Width %',
  'crop.height': 'Height %',

  'product.title': 'Help, Privacy, and Version',
  'product.intro': 'Bead Grid Studio Community Edition is an open-source, browser-local pattern tool. It has no in-app account, payment, license-key gate, or cloud image conversion.',
  'product.stepsTitle': 'Three Steps',
  'product.step1': 'Choose an image and crop to one subject when possible.',
  'product.step2': 'Verify the aspect ratio, physical board, color count, and bead count.',
  'product.step3': 'Export the per-cell pattern; use paginated printing for large patterns.',
  'product.privacyTitle': 'Privacy and Recovery',
  'product.privacyText': 'The app does not upload your source image. An unfinished pattern can be stored in this browser without the reference image, and you can clear it at any time. Your hosting provider may still keep basic access and security logs under its own policy.',
  'product.colorTitle': 'Color and Trademark Notice',
  'product.colorText': 'The default palette uses 221 standard color codes and does not represent any manufacturer official numbering. Screen HEX values are approximations; verify the physical color chart for the batch you will use.',
  'product.performanceTitle': 'Pegboards and Performance',
  'product.performanceText': '50-cell and 52-cell pegboards may be incompatible. Large patterns automatically limit zoom and export pixels to protect low-memory devices. No algorithm can recover details smaller than the target bead grid.',
  'product.repo': 'Open-source Repository',
  'product.privacy': 'Privacy',
  'product.terms': 'Terms',
  'product.version': 'Version',

  'conversion.title': 'Generating Fuse Bead Pattern',
  'conversion.analyzing': 'Analyzing image and palette…',
  'ready.kicker': 'Pattern ready 🎉',
  'ready.text': 'Review the pattern, color codes, and bead count before exporting or sharing.',
  'share.title': 'Fuse bead pattern made with Bead Grid Studio',
  'share.text': 'I made this fuse-bead pattern with Bead Grid Studio 🫘',
  'share.dialogTitle': 'Export Share Card',
  'share.dialogHelp': 'Share cards are created only when you ask. They do not alter the construction sheet or upload your source image.',
  'share.wide': '16:9 · X / Reddit / Discord',
  'share.portrait': '3:4 · Mobile / Community Posts',
  'share.download': 'Download Share Card',
  'share.cardTitle': 'Image → Fuse Bead Pattern',
  'share.cardSubtitle': 'Local • Editable • Printable • Open Source',
  'share.original': 'Original',
  'share.pattern': 'Bead Pattern',
  'share.formatLabel': 'Share-card format',
  'share.unavailable': 'This project has no reference image, so a Before / After share card cannot be created.',
  'share.copied': 'Link copied',
  'share.failed': 'Sharing is unavailable. The link was copied instead.',
  'share.cardFooter': 'github.com/zwhy149/bead-grid-studio',

  'aria.readyActions': 'Pattern-ready actions',
  'aria.communityActions': 'Open-source community actions',
  'aria.closeShare': 'Close share-card dialog',
  'aria.sharePreview': 'Share-card preview',
  'aria.localPromises': 'Local and offline features',
  'aria.chooseColor': 'Choose {code} {name}',
  'aria.copyStats': 'Copy every color code, count, and share as plain text for bead ordering',
  'aria.statShare': '{percent} of all beads',
  'aria.patternCanvas': '{cols} by {rows} fuse-bead pattern. Use arrow keys to move, Space to draw, and Delete to erase.',
  'title.color': '{code} {name} {hex}',
  'title.zoomLimit': 'Maximum zoom for this pattern: {percent}%',

  'size.patternAndBoard': 'Pattern {patternCols}×{patternRows} / Board {boardCols}×{boardRows}',
  'size.patternOnly': 'Pattern {cols}×{rows}',
  'size.boardCols': 'Total Board Columns',
  'size.boardRows': 'Total Board Rows',
  'size.aspectUnlockedInitial': 'Unlocked. Width and height can be set independently after choosing an image.',
  'size.aspectLocked': 'Locked to the source ratio {ratio}:1. A preset produces a proportional size such as {cols}×{rows}.',
  'size.aspectUnlocked': 'Aspect lock is off. Show Entire Image adds margins; Fill and Crop removes content.',
  'board.statusTitle': 'Board {boardCols}×{boardRows} · Pattern {patternCols}×{patternRows} (not stretched)',
  'board.statusBody': '{profile} × {count}; about {width}×{height} cm. Centered with {left}/{right} blank columns, {top}/{bottom} blank rows, and {blank} empty cells.',

  'punctuation.list': ', ',
  'quality.textTooSmall': 'Text is below a buildable size',
  'quality.aspectMismatch': 'Pattern aspect ratio mismatch',
  'quality.detailConflict': 'Some details exceed the current grid',
  'quality.outlineOnly': 'This size can preserve outlines only',
  'quality.structureOnlyShort': '{size} can preserve layout structure only; {action}',
  'quality.cropSuggestion': 'crop to a smaller area first',
  'quality.modeOrCropSuggestion': 'switch to Document Structure or crop first',
  'quality.aspectShort': '{cols}×{rows} does not match the source ratio; {result}',
  'quality.hasMargins': 'large margins are present',
  'quality.cropsContent': 'some source content will be cropped',
  'quality.detailShort': '{size}: {warning}; no extra beads were forced into the result',
  'quality.outlineShort': '{size} can preserve only the main outline and large fills. An algorithm cannot invent missing grid detail.',
  'quality.documentWarning': 'Document capacity warning',
  'quality.aspectWarning': 'Aspect and crop warning',
  'quality.cellConflict': 'Cell conflict',
  'quality.smallWarning': 'Small-grid detail warning',
  'quality.documentTitle': 'The full document exceeds the readable 100-cell range',
  'quality.aspectTitle': '{cols}×{rows} does not match the source ratio',
  'quality.photoDetailTitle': 'The current grid cannot express all photo detail',
  'quality.documentBody': 'At {size}, only the layout, long rules, and large fills remain; body text will not be readable. {modeAdvice}At {beadMm} mm per bead, the result is about {width}×{height} cm. Cropping one sub-image is most effective.',
  'quality.documentModeAdvice': 'Switch to Document Structure below. ',
  'quality.letterboxBody': 'The image is not stretched, but about {percent}% of the pattern is margin. Only about {cols}×{rows} cells carry image content. Prefer the source aspect ratio.',
  'quality.cropBody': 'Filling this canvas crops about {percent}% of the source. Subjects near an edge can disappear. Use the source ratio or crop the subject manually.',
  'quality.detailBody': '{warning}. Increase to the next size or use the brush to restore the detail.',
  'quality.photoBody': 'The effective long side is only about {long} cells ({cells} useful cells). Small text and edges will disappear. Increase the size or crop one subject.',
  'quality.sourcePixels': '{x}×{y} source pixels',
  'quality.glyphHeight': 'Body text is about {height} cells high',
  'quality.largeStructureOnly': 'Large structure only',
  'quality.marginPercent': '{percent}% margin',
  'quality.cropPercent': '{percent}% cropped',
  'quality.pixelsPerBead': 'About {count} pixels / bead',
  'quality.alreadySize': 'Already {cols}×{rows}',
  'quality.fixAspect': 'Fix ratio to {cols}×{rows}',
  'quality.increaseSize': 'Increase to {cols}×{rows} · about {width}×{height} cm at {beadMm} mm',
  'quality.cropChoose': 'Crop and Choose Subject',
  'quality.oneSubject': 'Make One Subject Only',
  'quality.fixAspectShort': 'Fix Aspect Ratio',
  'quality.cropSubject': 'Crop Subject',
  'quality.missingDetails': '{count} detail(s) are smaller than one cell',
  'quality.detailCollisions': '{count} detail collision(s) cannot be separated safely',
  'quality.detailIntegrity': '{details}; the app did not add fake beads or merge separate lines',
  'quality.marginVerb': 'leave margins',
  'quality.cropVerb': 'crop',

  'smart.documentUnreadable': 'A full page of text cannot become readable bead art',
  'smart.documentSummary': 'At most {cols}×{rows} can preserve layout and large fills. Crop the one region you want to make.',
  'smart.cropFirst': 'Crop One Region First',
  'smart.generatedWithWarning': 'Generated, but {warning}',
  'smart.generatedSmallLine': 'Small-grid line-art refinement is ready',
  'smart.generatedReview': 'Generated — review before exporting',
  'smart.trimmed': 'Removed about {percent}% of safe outer white space. You can restore the full source at any time. ',
  'smart.detailWarning': '{warning}; increase to {size} cells or restore it with the brush. ',
  'smart.readySummary': '{size} · {beads} beads · {colors} colors · source ratio preserved. {framing}{detailWarning}About {width}×{height} cm at {beadMm} mm. Material count equals every non-empty cell; verify physical colors before making.',
  'smart.regenerate': 'Generate Again',
  'smart.customTooSmall': 'Custom size is too small for more than major outlines',
  'smart.customAspect': 'Custom size does not match the source ratio',
  'smart.custom': 'Using custom settings',
  'smart.customIssues': 'Current {cols}×{rows}: {warning}. Increase to the next size or restore with the brush.',
  'smart.customCapacity': 'Current {cols}×{rows} has about {cells} useful cells. Sharpening cannot recover missing detail; increase size or crop the subject.',
  'smart.customMismatch': 'This will {effect} about {percent}%. Use Fix Aspect Ratio below.',
  'smart.customSummary': 'Current {cols}×{rows}, up to {colors} colors. You can restore the automatic recommendation at any time.',
  'smart.restore': 'Restore Automatic Recommendation',
  'smart.recommendedReady': 'Recommended setup ready',
  'smart.trimmedShort': 'outer white space reduced by about {percent}% · ',
  'smart.recommendedSummary': '{cols}×{rows} · {capacity} cells · up to {colors} colors · {trimmed}internal standard palette · calibrated black and white · per-cell codes',

  'unit.beads': '{formatted} beads',
  'unit.colors': '{count} colors',
  'unit.colorsValue': '{count} colors',
  'history.undoAvailable': '{label} · {count} undo step(s) available',
  'history.edited': 'edited the pattern',
  'history.stroke': 'completed one drawing stroke',
  'history.converted': 'converted the image',
  'file.details': '{width} × {height} · {size} MB{suffix}',
  'file.trimmed': ' · subject framed automatically',
  'file.cropped': 'Crop · {width}×{height} source pixels',
  'file.restored': 'Full source bounds restored',
  'crop.sourcePixels': '{width} × {height} source pixels',
  'conversion.progress': 'Processing in {mode} · up to {colors} colors…',
  'conversion.backgroundRemoved': ', connected border background removed',
  'conversion.colorsMerged': ', reduced from {before} to {after} colors',

  'status.generatingPixel': 'Generating a {cols}×{rows} pixel-accurate pattern…',
  'status.conversionBlocked': 'Conversion in progress · state-changing actions are blocked',
  'status.undo': 'Undid the previous action',
  'status.redo': 'Redid the previous action',
  'status.loadingSample': 'Loading the bundled sample…',
  'status.sampleFailed': 'Could not load the sample',
  'status.readingImage': 'Reading image…',
  'status.imageReadFailed': 'Could not read the image',
  'status.conversionComplete': 'Conversion complete · {mode} · {size} · {colors} colors',
  'status.documentPreview': '{kind} · body text is unreadable · {size} · {advice}',
  'status.structurePreview': 'Document-structure preview',
  'status.complexPreview': 'Complex-document preview',
  'status.cropSuggested': 'crop to one region first',
  'status.modeSuggested': 'switch to Document Structure or crop first',
  'status.smallRefine': 'Small-grid refinement complete · {warning} · increase the grid for more detail',
  'status.conversionFailed': '{reason} · restored the previous complete pattern',
  'status.timeout': 'Conversion timed out',
  'status.browserUnsupported': 'This browser cannot start a safe background conversion',
  'status.failed': 'Conversion failed',
  'status.boardPattern': 'pattern {patternCols}×{patternRows} / board {boardCols}×{boardRows}',
  'status.zoom': '{message} · zoom {percent}%',
  'status.zoomValue': 'Zoom {percent}%',

  'toast.chooseImageFirst': 'Choose an image first.',
  'toast.colorSelected': 'Selected {code} {name}',
  'toast.conversionBusy': 'The image is still converting. Wait for it to finish or cancel the conversion first.',
  'toast.sampleFailed': 'The bundled sample could not be loaded. Choose a local image instead.',
  'toast.imageTooLarge': 'This image is larger than 24 MB. Compress it and try again.',
  'toast.invalidImage': 'The image header is invalid or damaged. Use a standard PNG, JPG, WebP, or static GIF.',
  'toast.imagePixelsTooLarge': 'The image contains too many pixels. Reduce its dimensions and try again.',
  'toast.safeDecodeFailed': 'This browser cannot safely downscale and decode the large image. Compress it and try again.',
  'toast.imageDamaged': 'The image is damaged or unsupported. Use a standard PNG, JPG, WebP, or static GIF.',
  'toast.photoModeAuto': 'Detected a detailed photo and switched to Detailed Photo mode to avoid a long line-art conversion.',
  'toast.documentPreview': 'A document-structure preview is ready; body text will not remain readable.',
  'toast.documentDetected': 'Detected a complex document. Switch to Document Structure or crop to one region first.',
  'toast.conversionTimeout': 'Conversion took too long, so it was cancelled and the previous pattern was restored. Use Detailed Photo mode or reduce the image size.',
  'toast.workerUnsupported': 'This browser cannot run the safe background converter. Use a current version of Chrome, Edge, Firefox, or Safari.',
  'toast.conversionFailed': 'Conversion failed, and the previous complete pattern was restored.',
  'toast.conversionComplete': 'Conversion complete: {colors} colors{background}{merged}',
  'toast.generatedWarning': 'Generated, but {warning}. Increase the size or restore it with the brush.',
  'toast.cropTooSmall': 'The crop is too small. Select a larger area.',
  'toast.emptyPicker': 'An empty cell has no color code. Choose a colored cell.',
  'toast.batchReplaceNoop': 'Choose two different colors before replacing.',
  'toast.batchReplaceEmpty': 'No cells use the source color.',
  'toast.batchReplaceDone': 'Replaced {count} cells.',
  'toast.boardTooLarge': 'This board layout exceeds the 200-cell limit. Use fewer boards horizontally or vertically.',
  'toast.zoomLimit': 'To protect memory, this {cols}×{rows} pattern is limited to {percent}% zoom.',
  'toast.canvasOverview': 'The full pattern is visible. Zoom in before editing individual cells.',
  'toast.canvasFit': 'Zoomed to {percent}%',
  'toast.exportDone': 'Color-code build sheet exported.',
  'toast.exportMemory': 'This device cannot safely create one image this large. Reduce the size or use paged printing.',
  'toast.exportFailed': 'Export failed. Reduce the pattern size and try again.',
  'toast.draftStorage': 'Browser recovery storage is full. Save the editable project manually.',
  'toast.draftCleared': 'Local recovery record cleared.',
  'toast.statsCopied': 'Bead list copied. Paste it into your notes or an order message.',
  'toast.statsCopyFailed': 'Copy failed. Please note the material counts manually.',
  'toast.draftInvalid': 'The recovery record was invalid or damaged and has been cleared safely.',
  'toast.projectSaved': 'The editable project was written to the selected file. The source image is not included.',
  'toast.projectDownloadStarted': 'The project download started. Confirm it in your downloads list. The source image is not included.',
  'toast.projectRestored': 'The unfinished project was restored without its source image. Continue editing or save it.',
  'toast.projectMigrated': 'The legacy 64-color project was remapped to the compatible base 221 color codes.',
  'toast.projectLoaded': 'Project loaded.',
  'toast.projectTooLarge': 'The project file is empty or too large.',
  'toast.projectInvalid': 'The project format is invalid or its version is unsupported.',
  'toast.printFailed': 'Print preview failed. Reduce the size or export a PNG instead.',
  'toast.boardContainOnly': 'Real Board mode always preserves the whole image: the proportional pattern is centered without hidden cropping or stretching.',
  'toast.codesNeedZoom': 'This is an overview scale. Zoom to 100% to inspect cell codes; the exported build sheet always includes every code.',
  'toast.previewEditZoom': 'This is a full-pattern overview. Zoom in before editing.',
  'toast.conversionCanceled': 'Conversion canceled. The previous pattern was preserved.',
  'toast.noMaterials': 'This pattern does not contain any beads yet.',
  'toast.makingStarted': 'Making Assistant is on; the canvas now highlights the current code.',
  'toast.colorDone': '{code} marked complete. Moving to the next color.',
  'toast.colorUndone': '{code} marked as not complete.',
  'toast.makingReset': 'Making progress reset.',
  'toast.csvExported': 'Material list exported: {colors} colors, {beads} beads.',

  'status.boardSwitched': 'Real board specification changed',
  'status.sizeCleared': 'Pattern size changed and canvas cleared',
  'status.rotated': 'Rotated 90° clockwise',
  'status.mirroredH': 'Mirrored horizontally',
  'status.mirroredV': 'Mirrored vertically',
  'status.cleared': 'Pattern cleared',
  'status.canvasOverview': 'Full-pattern overview · zoom in to edit',
  'status.canvasFit': 'Pattern fitted to the window',
  'status.exporting': 'Generating high-resolution PNG…',
  'status.exportDone': 'Build-sheet PNG export complete',
  'status.exportFailed': 'PNG export failed',
  'status.draftCleared': 'Local recovery record cleared',
  'status.projectSaved': 'Project file saved successfully',
  'status.projectDownloadStarted': 'Project download started · the browser cannot confirm the final write',
  'status.projectRestored': 'Restored unfinished {cols}×{rows} project',
  'status.projectLoaded': 'Loaded {cols}×{rows} project{migration}',
  'status.projectMigration': ' · migrated to the compatible base 221 codes',
  'status.projectLoadFailed': 'Project load failed',
  'status.printReady': 'Prepared {count} print page(s) · do not enable a second “fit to page” scale in the print dialog',
  'status.printFailed': 'Print preview failed',
  'status.conversionCanceled': 'Conversion canceled and the previous complete pattern restored',
  'status.majorGrid': 'Major guides set to every {count} cells',
  'status.makingStarted': 'Making Assistant enabled · verify and track one color at a time',
  'status.makingStopped': 'Making Assistant closed · progress remains in the project',
  'status.csvExported': 'Material CSV exported · {colors} colors · {beads} beads',

  'history.boardChanged': 'changed the real board specification',
  'history.sizeCleared': 'changed the pattern size and cleared the canvas',
  'history.rotated': 'rotated 90° clockwise',
  'history.mirroredH': 'mirrored horizontally',
  'history.mirroredV': 'mirrored vertically',
  'history.cleared': 'cleared the pattern',
  'history.keyboardDraw': 'drew with the keyboard',
  'history.keyboardErase': 'erased with the keyboard',
  'history.batchReplace': 'replaced colors in bulk',

  'confirm.boardQualitySevere': '\n\nClarity warning: this board can preserve only the main outline and large fills; photo text and fine detail will not be readable.',
  'confirm.boardQualityLow': '\n\nClarity note: one board is suitable for simple cartoons. Use a 104-cell or larger layout for a complex photo.',
  'confirm.boardSwitch': 'Switch to {profile}, {tilesX}×{tilesY} boards and generate “Pattern {patternCols}×{patternRows} / Board {boardCols}×{boardRows}”? This replaces manual edits; a successful conversion can be undone.{quality}\n\nContinue?',
  'confirm.patternMode': 'Switch to Pattern Clarity mode and extract the {cols}×{rows} pattern from the board? This replaces manual edits; a successful conversion can be undone. Continue?',
  'confirm.resizeQuality': '\n\nClarity warning: only about {cells} useful cells remain. The result can preserve the main outline and large fills, but photo text and fine detail will disappear.',
  'confirm.resize': 'Change to {cols}×{rows} and regenerate from the source? This replaces manual edits; the complete current pattern can be restored with Undo.{quality}\n\nContinue?',
  'confirm.clear': 'Clear the current pattern? This action can be undone.',
  'confirm.resetMaking': 'Clear all color-by-color making progress? The pattern itself will not change.',
  'confirm.exportIssues': 'This pattern {issues}. Increase the size or restore details with the brush first. Export anyway?',
  'confirm.loadProject': 'The current pattern has unsaved changes. Loading another project will replace it. Select Cancel if you want to save first.\n\nDiscard the changes and continue?',
  'confirm.printPages': 'This {cols}×{rows} pattern will be split into up to {span}×{span} cells per page ({pages} pages). Each page keeps global coordinates, per-cell codes, guides, and page material counts. Continue?',
  'confirm.clearDraft': 'This removes only the browser recovery record, not downloaded files. Clear it?',
  'confirm.coverCrop': 'Filling this canvas crops about {percent}% of the source. Confirm that the subject is not near an edge. Continue with Fill and Crop?',

  'prompt.projectName': 'The project name is stored in JSON. Rename it here if the source filename contains a person or order number.',
  'project.defaultName': 'bead-pattern-project',
  'project.importedName': 'Imported Pattern',
  'project.loadedSubtitle': 'JSON project loaded · source image not included',
  'project.fileDescription': 'Bead Grid Studio editable project',
  'project.recoveryFile': 'recovery.bead.json',
  'file.patternFallback': 'bead-pattern',

  'export.header': '{title} · {cols} columns × {rows} rows · {colors} colors · {beads} beads',
  'export.boardCopy': '{profile} × {tilesX}×{tilesY} boards · Pattern {cols}×{rows}',
  'export.patternCopy': 'Pattern dimensions follow the source ratio',
  'export.subtitle': 'Internal standard color codes · {board} · guides every {step} cells · per-cell codes',
  'export.materials': 'Material List',
  'export.count': '{count} beads',
  'export.total': 'Total beads required: {count}',
  'export.boardSize': 'Pattern {patternCols}×{patternRows} · Board {boardCols}×{boardRows} · {boards} board(s)',
  'export.patternSize': '{cols} columns × {rows} rows',
  'export.sizeSummary': 'Pattern size: {size} · guides every {step} cells',
  'export.generated': 'Bead Grid Studio v{version} · generated locally',
  'print.pageTitle': '{title} · Page {page}/{pages}',
  'print.pageSubtitle': 'Full pattern {cols}×{rows} · columns {startCol}–{endCol} / rows {startRow}–{endRow} · internal standard color codes',
  'print.materials': 'Materials on This Page',
  'print.count': '{code} · {count} beads',
  'confirm.replaceImage': 'The current pattern has unsaved changes. Choosing another image will replace it. Select Cancel if you want to save the editable project first.\n\nDiscard the changes and continue?',
};

export default enUS;
